import { useState } from 'react';
import { createPortal } from 'react-dom';

import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import ColourPicker from './colour-picker';
import { ColourPickerPanel } from './colour-picker.style';
import ToolbarItem from './toolbar-item';
import { ToolBarTool } from './toolbar.style';
import * as Styled from './toolbar-item-shapes-styles.style';
import { SUB_MENU_PORTAL_ID } from '../draw-canvas.constants';

type Props = {
  fillColour: string;
  strokeColour: string;
  onSelectFill: (colour: string) => void;
  onSelectStroke: (colour: string) => void;
  showFill?: boolean;
};

const ToolbarItemShapeStyles = ({
  fillColour,
  strokeColour,
  onSelectFill,
  onSelectStroke,
  showFill = true,
}: Props) => {
  return (
    <>
      <ToolbarItemStroke
        strokeColour={strokeColour}
        onSelectStroke={onSelectStroke}
      />
      {showFill && (
        <ToolbarItemFill fillColour={fillColour} onSelectFill={onSelectFill} />
      )}
    </>
  );
};

const ToolbarItemStroke = ({
  strokeColour,
  onSelectStroke,
}: {
  strokeColour: string;
  onSelectStroke: (colour: string) => void;
}) => {
  const [isStrokeActive, setIsStrokeActive] = useState<boolean>(false);

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(isStrokeActive, setIsStrokeActive, {
      offset: [0, 8],
      placement: 'left-start',
      strategy: 'fixed',
    });

  const portalContainer = document.getElementById(SUB_MENU_PORTAL_ID);

  const renderStrokeColorPanel = () => (
    <ColourPickerPanel
      ref={setPopperElement}
      role="dialog"
      tabIndex={-1}
      style={styles.popper}
      {...attributes.popper}
    >
      <ColourPicker
        currentColour={strokeColour}
        onSelectColour={colour => {
          onSelectStroke(colour);
          setIsStrokeActive(false);
        }}
      />
    </ColourPickerPanel>
  );

  return (
    <>
      <ToolbarItem hoverText="Stroke colour">
        <ToolBarTool
          type="button"
          ref={setReferenceElement}
          $isActive={isStrokeActive}
          onClick={() => setIsStrokeActive(!isStrokeActive)}
        >
          <Styled.ToolBarItemShapesStroke $colour={strokeColour} />
        </ToolBarTool>
      </ToolbarItem>
      {isStrokeActive &&
        (portalContainer
          ? createPortal(renderStrokeColorPanel(), portalContainer)
          : renderStrokeColorPanel())}
    </>
  );
};

const ToolbarItemFill = ({
  fillColour,
  onSelectFill,
}: {
  fillColour: string;
  onSelectFill: (colour: string) => void;
}) => {
  const [isFillActive, setIsFillActive] = useState<boolean>(false);

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(isFillActive, setIsFillActive, {
      offset: [0, 8],
      placement: 'left-start',
      strategy: 'fixed',
    });

  const portalContainer = document.getElementById(SUB_MENU_PORTAL_ID);

  const renderFillColorPanel = () => (
    <ColourPickerPanel
      ref={setPopperElement}
      role="dialog"
      tabIndex={-1}
      style={styles.popper}
      {...attributes.popper}
    >
      <ColourPicker
        currentColour={fillColour}
        onSelectColour={colour => {
          onSelectFill(colour);
          setIsFillActive(false);
        }}
      />
    </ColourPickerPanel>
  );

  return (
    <>
      <ToolbarItem hoverText="Fill colour">
        <ToolBarTool
          type="button"
          ref={setReferenceElement}
          $isActive={isFillActive}
          onClick={() => setIsFillActive(!isFillActive)}
        >
          <Styled.ToolBarItemShapesFill $colour={fillColour} />
        </ToolBarTool>
      </ToolbarItem>
      {isFillActive &&
        (portalContainer
          ? createPortal(renderFillColorPanel(), portalContainer)
          : renderFillColorPanel())}
    </>
  );
};

export default ToolbarItemShapeStyles;
